import type { AccessOptions, PortalPasskey, PortalPushSubscription, PortalUser, PushSubscriptionStatus, SecurityEvidence, SessionInfo, Snapshot, UserRole } from "./types";

function apiUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  let message = fallback;
  try {
    const payload = await response.json();
    if (payload?.error) message = payload.error;
  } catch {
    message = `${fallback}: HTTP ${response.status}`;
  }
  throw new Error(message);
}

export async function fetchSnapshot(refresh = false): Promise<Snapshot> {
  const response = await fetch(apiUrl(`/api/snapshot${refresh ? "?refresh=1" : ""}`));
  return jsonOrError<Snapshot>(response, "Snapshot request failed");
}

export async function fetchSession(): Promise<SessionInfo | null> {
  const response = await fetch(apiUrl("/api/session"));
  if (response.status === 401) {
    return null;
  }
  return jsonOrError<SessionInfo>(response, "Session request failed");
}

export async function fetchAuthOptions(): Promise<{ ozoneSsoEnabled: boolean }> {
  const response = await fetch(apiUrl("/api/auth/options"));
  return jsonOrError<{ ozoneSsoEnabled: boolean }>(response, "Auth options request failed");
}

export async function login(username: string, password: string): Promise<SessionInfo> {
  const response = await fetch(apiUrl("/api/login"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username, password })
  });
  return jsonOrError<SessionInfo>(response, "Login failed");
}

export async function passkeyAuthenticationOptions(username: string) {
  const response = await fetch(apiUrl("/api/passkeys/authentication/options"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username })
  });
  return jsonOrError<{ options: import("@github/webauthn-json").CredentialRequestOptionsJSON }>(response, "Passkey sign-in request failed");
}

export async function verifyPasskeyAuthentication(credential: unknown): Promise<SessionInfo> {
  const response = await fetch(apiUrl("/api/passkeys/authentication/verify"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credential })
  });
  return jsonOrError<SessionInfo>(response, "Passkey sign-in failed");
}

export async function fetchPasskeys() {
  const response = await fetch(apiUrl("/api/passkeys"));
  return jsonOrError<{ passkeys: PortalPasskey[]; store: SessionInfo["passkeyStore"] }>(response, "Passkey list request failed");
}

export async function passkeyRegistrationOptions() {
  const response = await fetch(apiUrl("/api/passkeys/registration/options"), { method: "POST" });
  return jsonOrError<{ options: import("@github/webauthn-json").CredentialCreationOptionsJSON }>(response, "Passkey registration request failed");
}

export async function verifyPasskeyRegistration(credential: unknown) {
  const response = await fetch(apiUrl("/api/passkeys/registration/verify"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credential })
  });
  return jsonOrError<{ passkey: PortalPasskey }>(response, "Passkey registration failed");
}

export async function deletePasskey(credentialId: string) {
  const response = await fetch(apiUrl(`/api/passkeys/${encodeURIComponent(credentialId)}`), { method: "DELETE" });
  return jsonOrError<{ ok: boolean; credentialId: string }>(response, "Passkey deletion failed");
}

export async function fetchPushStatus() {
  const response = await fetch(apiUrl("/api/push/status"));
  return jsonOrError<PushSubscriptionStatus>(response, "Push status request failed");
}

export async function savePushSubscription(subscription: unknown) {
  const response = await fetch(apiUrl("/api/push/subscriptions"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ subscription })
  });
  return jsonOrError<{ subscription: PortalPushSubscription; status: PushSubscriptionStatus }>(response, "Push subscription failed");
}

export async function deletePushSubscription(endpoint?: string | null) {
  const response = await fetch(apiUrl("/api/push/subscriptions"), {
    method: "DELETE",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ endpoint })
  });
  return jsonOrError<{ ok: boolean; deleted: number; status: PushSubscriptionStatus }>(response, "Push unsubscribe failed");
}

export async function logout() {
  const response = await fetch(apiUrl("/api/logout"), { method: "POST" });
  return jsonOrError<{ ok: boolean }>(response, "Logout failed");
}

export async function fetchUsers() {
  const response = await fetch(apiUrl("/api/users"));
  return jsonOrError<{ users: PortalUser[]; store: SessionInfo["userStore"]; accessOptions: AccessOptions }>(response, "User list request failed");
}

export async function fetchSecurityEvidence(refresh = false) {
  const response = await fetch(apiUrl(`/api/security/evidence${refresh ? "?refresh=1" : ""}`));
  return jsonOrError<SecurityEvidence>(response, "Security evidence request failed");
}

export async function updateAccount(input: { email?: string; emailAlerts?: boolean }) {
  const response = await fetch(apiUrl("/api/account"), {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input)
  });
  return jsonOrError<{ user: PortalUser }>(response, "Account update failed");
}

export async function createUser(input: { username: string; email?: string; emailAlerts?: boolean; password: string; role: UserRole; portfolioIds: string[] }) {
  const response = await fetch(apiUrl("/api/users"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input)
  });
  return jsonOrError<{ user: PortalUser }>(response, "User creation failed");
}

export async function updateUser(username: string, input: { email?: string; emailAlerts?: boolean; password?: string; role?: UserRole; disabled?: boolean; portfolioIds?: string[] }) {
  const response = await fetch(apiUrl(`/api/users/${encodeURIComponent(username)}`), {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input)
  });
  return jsonOrError<{ user: PortalUser }>(response, "User update failed");
}

export async function deleteUser(username: string) {
  const response = await fetch(apiUrl(`/api/users/${encodeURIComponent(username)}`), { method: "DELETE" });
  return jsonOrError<{ ok: boolean; username: string }>(response, "User deletion failed");
}

export async function generateReport(period: "weekly" | "monthly", month?: string) {
  const params = new URLSearchParams({ period });
  if (month) params.set("month", month);
  const response = await fetch(apiUrl(`/api/reports/otap?${params.toString()}`));
  return jsonOrError<{ markdown: string; generatedAt: string; filename: string }>(response, "Report request failed");
}

export async function downloadReport(period: "weekly" | "monthly", month?: string) {
  const params = new URLSearchParams({ period, format: "pdf", download: "1" });
  if (month) params.set("month", month);
  const response = await fetch(apiUrl(`/api/reports/otap?${params.toString()}`));
  if (!response.ok) {
    throw new Error(`Report download failed: HTTP ${response.status}`);
  }

  const disposition = response.headers.get("content-disposition") || "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] || `kpn-fiam-monthly-availability-report-${month || "current"}.pdf`;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  return { filename };
}

export async function sendMonthlyReportEmail(month?: string, period: "weekly" | "monthly" = "monthly") {
  const response = await fetch(apiUrl("/api/reports/email/send"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ month, period })
  });
  return jsonOrError<{ ok: boolean; skipped?: boolean; reason?: string; missing?: string[]; accepted?: string[]; rejected?: string[] }>(
    response,
    "Report email failed"
  );
}

export async function sendIncidentToSlack(incidentId: string, channel: string) {
  const response = await fetch(apiUrl(`/api/incidents/${encodeURIComponent(incidentId)}/slack`), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ channel })
  });
  return jsonOrError<{ ok?: boolean; skipped?: boolean; reason?: string }>(response, "Slack request failed");
}

export async function resetService(environment: string, serviceId: string, reason?: string) {
  const response = await fetch(apiUrl(`/api/services/${encodeURIComponent(environment)}/${encodeURIComponent(serviceId)}/reset`), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      confirm: "RESET",
      reason: reason || "Manual reset from Protium Monitoring"
    })
  });
  return jsonOrError<{
    ok: boolean;
    action: "reset";
    service: {
      id: string;
      serviceId: string;
      serviceName: string;
      dshServiceName: string;
      environment: string;
      environmentLabel: string;
      portfolioId: string;
    };
    dsh: { ok: boolean; status: number; method: string; path: string; requestedAt: string };
  }>(response, "Service reset failed");
}

export async function setSlackAlertsEnabled(enabled: boolean) {
  const response = await fetch(apiUrl("/api/slack/alerts"), {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      enabled,
      reason: enabled ? "Alerts resumed from Protium Monitoring" : "Alerts paused from Protium Monitoring"
    })
  });
  return jsonOrError<Snapshot["integrationHealth"]["slack"]["control"]>(response, "Slack alert setting failed");
}
